import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ConnectButton, useCurrentAccount } from '@mysten/dapp-kit';
import Overview from './pages/Overview';
import Staking from './pages/Staking';
import Governance from './pages/Governance';
import Whitepaper from './pages/Whitepaper';
import './i18n/config';
import './styles/agu.css';

export default function App() {
  const { t, i18n } = useTranslation();
  const [showLangMenu, setShowLangMenu] = useState(false);
  const account = useCurrentAccount();
  const walletAddress = account?.address ?? null;

  const languages = useMemo(
    () => [
      { code: 'en', name: 'English', flag: '🌍' },
      { code: 'ko', name: '한국어', flag: '🇰🇷' },
      { code: 'zh', name: '中文', flag: '🇨🇳' },
      { code: 'ru', name: 'Русский', flag: '🇷🇺' },
      { code: 'es', name: 'Español', flag: '🇪🇸' },
      { code: 'fr', name: 'Français', flag: '🇫🇷' },
      { code: 'id', name: 'Bahasa Indonesia', flag: '🇮🇩' },
      { code: 'pt', name: 'Português', flag: '🇵🇹' },
      { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳' },
      { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
      { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
      { code: 'uk', name: 'Українська', flag: '🇺🇦' },
    ],
    []
  );

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setShowLangMenu(false);
    localStorage.setItem('language', lng);
  };

  useEffect(() => {
    const savedLang = localStorage.getItem('language');
    if (savedLang) {
      i18n.changeLanguage(savedLang);
    }
  }, []);

  return (
    <Router>
      <div className="agu-app">
        <header className="agu-nav">
          <div className="agu-nav-inner">
            <div className="agu-brand">
              <span className="agu-brand-icon">🪙</span>
              <span className="agu-brand-text">AGU</span>
            </div>
            <nav className="agu-nav-links">
              <NavLink
                className={({ isActive }) => `agu-nav-link${isActive ? ' active' : ''}`}
                to="/"
              >
                {t('nav.overview')}
              </NavLink>
              <NavLink
                className={({ isActive }) => `agu-nav-link${isActive ? ' active' : ''}`}
                to="/staking"
              >
                {t('nav.staking')}
              </NavLink>
              <NavLink
                className={({ isActive }) => `agu-nav-link${isActive ? ' active' : ''}`}
                to="/governance"
              >
                {t('nav.governance')}
              </NavLink>
              <NavLink
                className={({ isActive }) => `agu-nav-link${isActive ? ' active' : ''}`}
                to="/whitepaper"
              >
                {t('nav.whitepaper')}
              </NavLink>
            </nav>
            <div className="agu-nav-actions">
              <div className="agu-lang-selector">
                <button
                  className="agu-lang-toggle"
                  onClick={() => setShowLangMenu(!showLangMenu)}
                >
                  <span className="agu-lang-icon">🌐</span>
                  {languages.find(l => l.code === i18n.language)?.name || 'English'}
                </button>
                {showLangMenu && (
                  <div className="agu-lang-menu">
                    {languages.map(lang => (
                      <button
                        key={lang.code}
                        className={`agu-lang-item ${i18n.language === lang.code ? 'active' : ''}`}
                        onClick={() => changeLanguage(lang.code)}
                      >
                        <span className="agu-lang-flag">{lang.flag}</span>
                        {lang.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="agu-wallet-connect">
                <ConnectButton />
              </div>
            </div>
          </div>
        </header>
        <main className="agu-main">
          <Routes>
            <Route path="/" element={<Overview />} />
            <Route path="/staking" element={<Staking />} />
            <Route path="/governance" element={<Governance />} />
            <Route path="/whitepaper" element={<Whitepaper />} />
          </Routes>
        </main>
        <footer className="agu-footer">
          <div className="agu-footer-inner">
            <div className="agu-footer-brand">
              <span className="agu-brand-icon">🪙</span>
              <span>AGU — Agent Governance Unit</span>
            </div>
            <div className="agu-footer-links">
              <a href="https://market.agentumi.xyz" target="_blank" rel="noopener noreferrer">Market</a>
              <a href="https://ledger.agentumi.xyz" target="_blank" rel="noopener noreferrer">Ledger</a>
              <a href="https://finance.agentumi.xyz" target="_blank" rel="noopener noreferrer">Finance</a>
              <a href="https://insurance.agentumi.xyz" target="_blank" rel="noopener noreferrer">Insurance</a>
              <a href="https://doc.agentumi.xyz" target="_blank" rel="noopener noreferrer">Docs</a>
              <a href="https://github.com/softkid/agentumi" target="_blank" rel="noopener noreferrer">GitHub</a>
            </div>
            <div className="agu-footer-copy">© 2026 AGENTUMI • AI Agent Economy on Blockchain</div>
          </div>
        </footer>
      </div>
    </Router>
  );
}
